import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './newblog.scss';

// ─── UI Components (already used in your admin) ──────────────────────────
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';

// ─── MUI Icons ─────────────────────────────────────────────────────────────
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// ─── Constants ─────────────────────────────────────────────────────────────
const CATEGORIES = [
    'Culture et patrimoine du Maroc',
    'Shopping au Maroc',
    'Gastronomie marocaine',
    'Artisanat marocain',
    'Voyage et aventure',
    'Événements',
];

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'published', label: 'Publié' },
];

const BLOCK_TYPES = [
    { type: 'paragraph', label: 'Paragraphe' },
    { type: 'heading', label: 'Titre' },
    { type: 'cta', label: 'CTA' },
];

// ─── Small inline styles for the new insert / reorder UI ──────────────────
// (kept inline so you don't have to touch newblog.scss — feel free to move
// these into your SCSS file later using the same class names)
const insertGapStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '18px',
    margin: '2px 0',
};
const insertLineStyle = {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    borderTop: '1px dashed #d0d0d8',
};
const insertBtnStyle = {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: 'none',
    background: '#fff',
    color: '#7a5cff',
    cursor: 'pointer',
    boxShadow: '0 0 0 1px #e0e0ea',
};
const insertMenuStyle = {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    gap: '6px',
    justifyContent: 'center',
    padding: '6px 0',
};
const insertMenuBtnStyle = {
    fontSize: '12px',
    padding: '4px 10px',
    borderRadius: '999px',
    border: '1px solid #d0d0d8',
    background: '#f7f7fb',
    cursor: 'pointer',
};
const blockOrderControlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
};
const smallIconBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#6b6b76',
};

// ─── Component ─────────────────────────────────────────────────────────────
const NewBlog = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // if id exists → edit mode
    const isEditing = Boolean(id);

    // ── Form state ──
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [category, setCategory] = useState('');
    const [author, setAuthor] = useState('Moroccool Team');
    const [status, setStatus] = useState('published');
    const [featured, setFeatured] = useState(false);

    // ── Media ──
    const [heroImage, setHeroImage] = useState('');
    const [heroPreview, setHeroPreview] = useState(null);
    const [uploadingHero, setUploadingHero] = useState(false);

    const [gallery, setGallery] = useState([]); // array of { src, alt, caption }
    const [uploadingGallery, setUploadingGallery] = useState(false);

    // ── Content blocks ──
    const [content, setContent] = useState([]);
    // index (0..content.length) where the "+" insert menu is currently open, or null
    const [insertMenuIndex, setInsertMenuIndex] = useState(null);

    // ── Tags ──
    const [tags, setTags] = useState([]);
    const [newTag, setNewTag] = useState('');

    // ── Related articles ──
    const [allArticles, setAllArticles] = useState([]);
    const [relatedArticles, setRelatedArticles] = useState([]); // array of ids

    // ── UI state ──
    const [collapsed, setCollapsed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [categoryMode, setCategoryMode] = useState('select'); // 'select' | 'custom'
    const [customCategory, setCustomCategory] = useState('');


    const dynamicCategories = useMemo(() => {
        const fromArticles = allArticles.map((a) => a.category).filter(Boolean);
        return Array.from(new Set([...CATEGORIES, ...fromArticles, category].filter(Boolean))).sort();
    }, [allArticles, category]);

    // ── Load article if editing ──
    useEffect(() => {
        if (isEditing) {
            const fetchArticle = async () => {
                try {
                    const res = await axios.get(
                        `https://api.moroccool.com/api/blogs/${id}`
                    );
                    const data = res.data;
                    setTitle(data.title || '');
                    setSubtitle(data.subtitle || '');
                    setExcerpt(data.excerpt || '');
                    setCategory(data.category || '');
                    setAuthor(data.author || 'Moroccool Team');
                    setStatus(data.status || 'draft');
                    setFeatured(data.featured || false);
                    setHeroImage(data.heroImage || '');
                    setGallery(data.gallery || []);
                    setContent(data.content || []);
                    setTags(data.tags || []);
                    setRelatedArticles(
                        data.relatedArticles?.map((a) => a._id) || []
                    );
                } catch (err) {
                    console.error('Error loading article:', err);
                    setError('Impossible de charger l’article.');
                }
            };
            fetchArticle();
        }
    }, [id, isEditing]);

    // ── Load all articles for related selection ──
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const res = await axios.get(
                    `https://api.moroccool.com/api/blogs?limit=100&status=published`
                );
                setAllArticles(res.data.blogs || []);
            } catch (err) {
                console.error('Error loading articles list:', err);
            }
        };
        fetchAll();
    }, []);

    // ── Handlers for content blocks ──────────────────────────────────────
    // `atIndex` lets you insert a block at any position instead of always
    // pushing to the end. Pass null (or omit) to append at the end.
    const addContentBlock = (type, atIndex = null) => {
        const newBlock = { type, text: '' };
        if (type === 'heading') newBlock.level = 2;
        if (type === 'cta') newBlock.url = '';

        setContent((prev) => {
            const updated = [...prev];
            if (atIndex === null || atIndex === undefined || atIndex >= updated.length) {
                updated.push(newBlock);
            } else {
                updated.splice(atIndex, 0, newBlock);
            }
            return updated;
        });
        setInsertMenuIndex(null);
    };

    const updateContentBlock = (index, field, value) => {
        const updated = [...content];
        updated[index] = { ...updated[index], [field]: value };
        setContent(updated);
    };

    const removeContentBlock = (index) => {
        setContent(content.filter((_, i) => i !== index));
    };

    // Move a block up (-1) or down (+1)
    const moveContentBlock = (index, direction) => {
        setContent((prev) => {
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= prev.length) return prev;
            const updated = [...prev];
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            return updated;
        });
    };

    const duplicateContentBlock = (index) => {
        setContent((prev) => {
            const updated = [...prev];
            updated.splice(index + 1, 0, { ...prev[index] });
            return updated;
        });
    };

    // ── Handlers for tags ──
    const addTag = () => {
        const trimmed = newTag.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
            setNewTag('');
        }
    };

    const removeTag = (tag) => {
        setTags(tags.filter((t) => t !== tag));
    };

    // ── Handlers for gallery ──
    const addGalleryImage = (src, alt = '', caption = '') => {
        setGallery([...gallery, { src, alt, caption }]);
    };

    const removeGalleryImage = (index) => {
        setGallery(gallery.filter((_, i) => i !== index));
    };

    const updateGalleryImage = (index, field, value) => {
        const updated = [...gallery];
        updated[index][field] = value;
        setGallery(updated);
    };

    // ── Upload handlers (using your existing /upload endpoints) ──
    const handleHeroUpload = async (file) => {
        if (!file) return;
        setHeroPreview(URL.createObjectURL(file));
        setUploadingHero(true);
        const formData = new FormData();
        formData.append('image', file);
        try {
            const res = await axios.post(
                `https://api.moroccool.com/api/upload/blog/image`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setHeroImage(res.data.imageUrl);
        } catch (err) {
            console.error('Hero upload failed:', err);
            setError('Erreur lors de l’upload de l’image principale.');
        } finally {
            setUploadingHero(false);
        }
    };

    const handleGalleryUpload = async (files) => {
        if (!files.length) return;
        setUploadingGallery(true);
        const formData = new FormData();
        Array.from(files).forEach((file) => formData.append('images', file));
        try {
            const res = await axios.post(
                `https://api.moroccool.com/api/upload/blog/images`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            res.data.imageUrls.forEach((url) => addGalleryImage(url));
        } catch (err) {
            console.error('Gallery upload failed:', err);
            setError('Erreur lors de l’upload de la galerie.');
        } finally {
            setUploadingGallery(false);
        }
    };

    // ── Submit ──
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (uploadingHero || uploadingGallery) {
            setError('Merci d\'attendre la fin de l\'upload avant de publier.');
            return;
        }
        if (!heroImage) {
            setError('L\'image principale est requise.');
            return;
        }

        setLoading(true);
        setError('');

        const payload = {
            title,
            subtitle,
            excerpt,
            category,
            author,
            status,
            featured,
            heroImage,
            gallery,
            content,
            tags,
            relatedArticles,
        };

        try {
            const url = isEditing
                ? `https://api.moroccool.com/api/blogs/${id}`
                : `https://api.moroccool.com/api/blogs`;
            const method = isEditing ? 'put' : 'post';
            await axios[method](url, payload);
            navigate('/blogs'); // adapt to your route for listing
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.response?.data?.error || 'Erreur lors de l’enregistrement.');
        } finally {
            setLoading(false);
        }
    };

    // ── Delete ──
    const handleDelete = async () => {
        if (!window.confirm('Voulez‑vous vraiment supprimer cet article ?')) return;
        try {
            await axios.delete(`https://api.moroccool.com/api/blogs/${id}`);
            navigate('/blogs');
        } catch (err) {
            console.error('Delete error:', err);
            setError('Erreur lors de la suppression.');
        }
    };

    // ── Small reusable "insert here" control ──
    const InsertGap = ({ atIndex }) => (
        <div style={insertGapStyle}>
            <div style={insertLineStyle} />
            {insertMenuIndex === atIndex ? (
                <div style={insertMenuStyle}>
                    {BLOCK_TYPES.map((bt) => (
                        <button
                            key={bt.type}
                            type="button"
                            style={insertMenuBtnStyle}
                            onClick={() => addContentBlock(bt.type, atIndex)}
                        >
                            {bt.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        style={{ ...insertMenuBtnStyle, color: '#a33' }}
                        onClick={() => setInsertMenuIndex(null)}
                    >
                        Annuler
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    style={insertBtnStyle}
                    title="Insérer un bloc ici"
                    onClick={() => setInsertMenuIndex(atIndex)}
                >
                    <AddCircleOutlineIcon fontSize="small" />
                </button>
            )}
        </div>
    );

    // ── Render ──
    return (
        <div className="blog-admin">
            <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            <div className="blog-main">
                <Navbar />

                <header className="blog-header">
                    <p className="blog-header-eyebrow">Moroccool Dashboard</p>
                    <h1 className="blog-header-title">
                        <strong>{isEditing ? 'Modifier' : 'Nouvel'}</strong> article
                    </h1>
                </header>

                <div className="blog-content">
                    <form className="blog-form" onSubmit={handleSubmit}>
                        {error && <div className="blog-error">{error}</div>}

                        {/* ─── SECTION 01 · MÉTADONNÉES ────────────────────────────── */}
                        <section className="blog-section">
                            <div className="blog-section-head">
                                <div className="blog-section-meta">
                                    <span className="blog-section-num">01</span>
                                    <h2 className="blog-section-title">Métadonnées</h2>
                                    <p className="blog-section-desc">
                                        Titre, extrait, catégorie et statut
                                    </p>
                                </div>
                                <div className="blog-section-body">
                                    <div className="blog-field">
                                        <label className="blog-label">Titre *</label>
                                        <input
                                            className="blog-input"
                                            type="text"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            required
                                            placeholder="Titre accrocheur de l’article"
                                        />
                                    </div>

                                    <div className="blog-field">
                                        <label className="blog-label">Sous‑titre</label>
                                        <input
                                            className="blog-input"
                                            type="text"
                                            value={subtitle}
                                            onChange={(e) => setSubtitle(e.target.value)}
                                            placeholder="Une courte phrase d’accroche"
                                        />
                                    </div>

                                    <div className="blog-field">
                                        <label className="blog-label">Extrait</label>
                                        <textarea
                                            className="blog-textarea"
                                            value={excerpt}
                                            onChange={(e) => setExcerpt(e.target.value)}
                                            rows="3"
                                            placeholder="Résumé de l’article (affiché dans les listes)"
                                        />
                                    </div>

                                    <div className="blog-row">
                                        <div className="blog-field half">
                                            <label className="blog-label">Catégorie</label>
                                            {categoryMode === 'select' ? (
                                                <select
                                                    className="blog-select"
                                                    value={category}
                                                    onChange={(e) => {
                                                        if (e.target.value === '__custom__') {
                                                            setCategoryMode('custom');
                                                            setCustomCategory('');
                                                            setCategory('');
                                                        } else {
                                                            setCategory(e.target.value);
                                                        }
                                                    }}
                                                >
                                                    <option value="">Sélectionner</option>
                                                    {dynamicCategories.map((cat) => (
                                                        <option key={cat} value={cat}>
                                                            {cat}
                                                        </option>
                                                    ))}
                                                    <option value="__custom__">+ Nouvelle catégorie…</option>
                                                </select>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <input
                                                        className="blog-input"
                                                        type="text"
                                                        autoFocus
                                                        placeholder="Nom de la nouvelle catégorie"
                                                        value={customCategory}
                                                        onChange={(e) => {
                                                            setCustomCategory(e.target.value);
                                                            setCategory(e.target.value);
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        style={smallIconBtnStyle}
                                                        title="Revenir à la liste"
                                                        onClick={() => {
                                                            setCategoryMode('select');
                                                            setCategory('');
                                                            setCustomCategory('');
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="blog-field half">
                                            <label className="blog-label">Auteur</label>
                                            <input
                                                className="blog-input"
                                                type="text"
                                                value={author}
                                                onChange={(e) => setAuthor(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="blog-row">
                                        <div className="blog-field half">
                                            <label className="blog-label">Statut</label>
                                            <div className="blog-toggle-group">
                                                {STATUS_OPTIONS.map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        className={`blog-toggle-btn ${status === opt.value ? 'active' : ''
                                                            }`}
                                                        onClick={() => setStatus(opt.value)}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="blog-field half">
                                            <label className="blog-label">Mise en avant</label>
                                            <div className="blog-toggle-group">
                                                <button
                                                    type="button"
                                                    className={`blog-toggle-btn ${featured === false ? 'active' : ''
                                                        }`}
                                                    onClick={() => setFeatured(false)}
                                                >
                                                    Standard
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`blog-toggle-btn ${featured === true ? 'active' : ''
                                                        }`}
                                                    onClick={() => setFeatured(true)}
                                                >
                                                    ⭐ Vedette
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ─── SECTION 02 · IMAGES ─────────────────────────────────── */}
                        <section className="blog-section">
                            <div className="blog-section-head">
                                <div className="blog-section-meta">
                                    <span className="blog-section-num">02</span>
                                    <h2 className="blog-section-title">Images</h2>
                                    <p className="blog-section-desc">
                                        Image principale et galerie
                                    </p>
                                </div>
                                <div className="blog-section-body">
                                    {/* Hero image */}
                                    <div className="blog-field">
                                        <label className="blog-label">Image principale *</label>
                                        <div className="blog-upload-zone">
                                            {heroPreview || heroImage ? (
                                                <div className="blog-preview-wrap">
                                                    <img
                                                        src={heroPreview || heroImage}
                                                        alt="Hero"
                                                        className="blog-preview-hero"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="blog-remove-btn"
                                                        onClick={() => {
                                                            setHeroImage('');
                                                            setHeroPreview(null);
                                                        }}
                                                    >
                                                        <CloseIcon />
                                                    </button>
                                                </div>
                                            ) : (
                                                <label className="blog-upload-label">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) =>
                                                            handleHeroUpload(e.target.files[0])
                                                        }
                                                    />
                                                    <CloudUploadOutlinedIcon className="blog-upload-icon" />
                                                    <span className="blog-upload-text">
                                                        Cliquer ou glisser‑déposer
                                                    </span>
                                                    <span className="blog-upload-sub">
                                                        PNG, JPG, WebP · max 5 Mo
                                                    </span>
                                                </label>
                                            )}
                                            {uploadingHero && (
                                                <div className="blog-progress">Upload en cours…</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Gallery */}
                                    <div className="blog-field">
                                        <label className="blog-label">Galerie d’images</label>
                                        <div className="blog-upload-zone">
                                            <label className="blog-upload-label">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={(e) =>
                                                        handleGalleryUpload(e.target.files)
                                                    }
                                                />
                                                <CloudUploadOutlinedIcon className="blog-upload-icon" />
                                                <span className="blog-upload-text">
                                                    Ajouter des images à la galerie
                                                </span>
                                                <span className="blog-upload-sub">
                                                    Plusieurs fichiers autorisés
                                                </span>
                                            </label>
                                            {uploadingGallery && (
                                                <div className="blog-progress">
                                                    Téléchargement des images…
                                                </div>
                                            )}
                                        </div>

                                        {gallery.length > 0 && (
                                            <div className="blog-gallery-grid">
                                                {gallery.map((img, idx) => (
                                                    <div key={idx} className="blog-gallery-item">
                                                        <img
                                                            src={img.src}
                                                            alt={img.alt || ''}
                                                            className="blog-gallery-img"
                                                        />
                                                        <div className="blog-gallery-overlay">
                                                            <input
                                                                type="text"
                                                                placeholder="Alt"
                                                                value={img.alt || ''}
                                                                onChange={(e) =>
                                                                    updateGalleryImage(idx, 'alt', e.target.value)
                                                                }
                                                            />
                                                            <input
                                                                type="text"
                                                                placeholder="Légende"
                                                                value={img.caption || ''}
                                                                onChange={(e) =>
                                                                    updateGalleryImage(
                                                                        idx,
                                                                        'caption',
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                            <button
                                                                type="button"
                                                                className="blog-remove-btn"
                                                                onClick={() => removeGalleryImage(idx)}
                                                            >
                                                                <CloseIcon />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ─── SECTION 03 · CONTENU ────────────────────────────────── */}
                        <section className="blog-section">
                            <div className="blog-section-head">
                                <div className="blog-section-meta">
                                    <span className="blog-section-num">03</span>
                                    <h2 className="blog-section-title">Contenu</h2>
                                    <p className="edit-blog-tip">
                                        Pour un lien dans un bloc <strong>paragraphe</strong> ou <strong>titre</strong> utilisez la syntaxe :
                                        <code>[Link Text](URL Link)</code>
                                    </p>
                                </div>
                                <div className="blog-section-body">
                                    <div className="blog-content-editor">
                                        <div className="blog-editor-toolbar">
                                            <button
                                                type="button"
                                                className="blog-toolbar-btn"
                                                onClick={() => addContentBlock('paragraph')}
                                            >
                                                + Paragraphe
                                            </button>
                                            <button
                                                type="button"
                                                className="blog-toolbar-btn"
                                                onClick={() => addContentBlock('heading')}
                                            >
                                                + Titre
                                            </button>
                                            <button
                                                type="button"
                                                className="blog-toolbar-btn"
                                                onClick={() => addContentBlock('cta')}
                                            >
                                                + CTA
                                            </button>
                                        </div>

                                        <div className="blog-blocks">
                                            {content.length === 0 && (
                                                <p className="blog-blocks-empty">
                                                    Aucun bloc pour le moment. Utilisez les boutons ci‑dessus pour ajouter du contenu.
                                                </p>
                                            )}

                                            {content.length > 0 && <InsertGap atIndex={0} />}

                                            {content.map((block, idx) => (
                                                <React.Fragment key={idx}>
                                                    <div className="blog-block">
                                                        <div className="blog-block-header">
                                                            <span className="blog-block-type">
                                                                {block.type === 'paragraph' && 'Paragraphe'}
                                                                {block.type === 'heading' && `Titre (h${block.level || 2})`}
                                                                {block.type === 'cta' && 'Appel à l’action'}
                                                            </span>
                                                            <div style={blockOrderControlsStyle}>
                                                                <button
                                                                    type="button"
                                                                    style={smallIconBtnStyle}
                                                                    title="Monter"
                                                                    disabled={idx === 0}
                                                                    onClick={() => moveContentBlock(idx, -1)}
                                                                >
                                                                    <ArrowUpwardIcon fontSize="small" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    style={smallIconBtnStyle}
                                                                    title="Descendre"
                                                                    disabled={idx === content.length - 1}
                                                                    onClick={() => moveContentBlock(idx, 1)}
                                                                >
                                                                    <ArrowDownwardIcon fontSize="small" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    style={smallIconBtnStyle}
                                                                    title="Dupliquer"
                                                                    onClick={() => duplicateContentBlock(idx)}
                                                                >
                                                                    <ContentCopyIcon fontSize="small" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="blog-block-remove"
                                                                    onClick={() => removeContentBlock(idx)}
                                                                >
                                                                    <DeleteOutlineIcon />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="blog-block-body">
                                                            {block.type === 'heading' && (
                                                                <select
                                                                    className="blog-select blog-block-select"
                                                                    value={block.level || 2}
                                                                    onChange={(e) =>
                                                                        updateContentBlock(
                                                                            idx,
                                                                            'level',
                                                                            parseInt(e.target.value)
                                                                        )
                                                                    }
                                                                >
                                                                    {[1, 2, 3, 4, 5, 6].map((l) => (
                                                                        <option key={l} value={l}>
                                                                            Niveau {l}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                            {block.type === 'cta' && (
                                                                <input
                                                                    className="blog-input"
                                                                    type="text"
                                                                    placeholder="URL (ex: /culture)"
                                                                    value={block.url || ''}
                                                                    onChange={(e) =>
                                                                        updateContentBlock(idx, 'url', e.target.value)
                                                                    }
                                                                />
                                                            )}
                                                            <textarea
                                                                className="blog-textarea blog-block-textarea"
                                                                placeholder={
                                                                    block.type === 'cta'
                                                                        ? 'Texte du bouton'
                                                                        : 'Texte… (astuce : [texte du lien](/url) pour un lien inline)'
                                                                }
                                                                value={block.text || ''}
                                                                onChange={(e) =>
                                                                    updateContentBlock(idx, 'text', e.target.value)
                                                                }
                                                                rows={block.type === 'paragraph' ? 3 : 1}
                                                            />
                                                        </div>
                                                    </div>
                                                    <InsertGap atIndex={idx + 1} />
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ─── SECTION 04 · TAGS ────────────────────────────────────── */}
                        <section className="blog-section">
                            <div className="blog-section-head">
                                <div className="blog-section-meta">
                                    <span className="blog-section-num">04</span>
                                    <h2 className="blog-section-title">Tags</h2>
                                    <p className="blog-section-desc">Mots‑clés pour le référencement</p>
                                </div>
                                <div className="blog-section-body">
                                    <div className="blog-tags">
                                        <div className="blog-tags-list">
                                            {tags.map((tag) => (
                                                <span key={tag} className="blog-tag">
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        className="blog-tag-remove"
                                                        onClick={() => removeTag(tag)}
                                                    >
                                                        <CloseIcon />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <div className="blog-tags-input">
                                            <input
                                                className="blog-input"
                                                type="text"
                                                placeholder="Ajouter un tag"
                                                value={newTag}
                                                onChange={(e) => setNewTag(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addTag();
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                className="blog-btn-add"
                                                onClick={addTag}
                                            >
                                                <AddIcon />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ─── SECTION 05 · ARTICLES SUGGÉRÉS ────────────────────── */}
                        <section className="blog-section">
                            <div className="blog-section-head">
                                <div className="blog-section-meta">
                                    <span className="blog-section-num">05</span>
                                    <h2 className="blog-section-title">Articles suggérés</h2>
                                    <p className="blog-section-desc">
                                        Liens vers d’autres articles du blog
                                    </p>
                                </div>
                                <div className="blog-section-body">
                                    <div className="blog-field">
                                        <label className="blog-label">Sélectionner des articles</label>
                                        <select
                                            className="blog-select blog-multiselect"
                                            multiple
                                            value={relatedArticles}
                                            onChange={(e) =>
                                                setRelatedArticles(
                                                    Array.from(
                                                        e.target.selectedOptions,
                                                        (opt) => opt.value
                                                    )
                                                )
                                            }
                                        >
                                            {allArticles.map((article) => (
                                                <option key={article._id} value={article._id}>
                                                    {article.title}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="blog-helper">
                                            Maintenez Ctrl/Cmd pour sélectionner plusieurs
                                        </small>
                                    </div>
                                    {relatedArticles.length > 0 && (
                                        <div className="blog-selected-articles">
                                            {relatedArticles.map((id) => {
                                                const article = allArticles.find((a) => a._id === id);
                                                return article ? (
                                                    <span key={id} className="blog-selected-article">
                                                        {article.title}
                                                        <button
                                                            type="button"
                                                            className="blog-remove-btn"
                                                            onClick={() =>
                                                                setRelatedArticles(
                                                                    relatedArticles.filter((rid) => rid !== id)
                                                                )
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    </span>
                                                ) : null;
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* ─── SUBMIT ─────────────────────────────────────────────────── */}
                        <div className="blog-submit-zone">
                            <div className="blog-submit-left">
                                {isEditing && (
                                    <button
                                        type="button"
                                        className="blog-btn-delete"
                                        onClick={handleDelete}
                                    >
                                        Supprimer l’article
                                    </button>
                                )}
                            </div>
                            <div className="blog-submit-right">
                                <button
                                    type="button"
                                    className="blog-btn-cancel"
                                    onClick={() => navigate('/blogs')}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="blog-btn-submit"
                                    disabled={loading || uploadingHero || uploadingGallery}
                                >
                                    <CheckCircleOutlineIcon />
                                    {loading
                                        ? 'Enregistrement…'
                                        : uploadingHero || uploadingGallery
                                            ? 'Upload en cours…'
                                            : isEditing
                                                ? 'Mettre à jour'
                                                : 'Publier'}
                                    <ArrowForwardIcon />
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <footer className="blog-footer">
                    © {new Date().getFullYear()} <span>Moroccool</span> — Tous droits réservés.
                </footer>
            </div>
        </div>
    );
};

export default NewBlog;